import { z } from 'zod';
import { accountTypeSchema, realEstateAccountTypeSchema } from './schemas/index.ts';
import type { Customer } from './types/customers.ts';
import {
  pagingFields,
  parseSearchParams,
  searchCount,
  type SearchFieldKinds,
  searchMoney,
  searchText,
  toSearchParams,
} from './search-spec.ts';

/**
 * Customer search filter spec (Smart Search, MET-3525).
 *
 * Same contract shape as the meter spec: the translator emits it,
 * `GET /customers/filter` runs it, and it round-trips through the URL.
 * Fields mirror what the customers list already exposes (see
 * `packages/api/src/db/sql/list-customers.sql`).
 */

// Sort columns any VIEW_CUSTOMERS caller may use.
const CUSTOMER_BASE_SORT_COLUMNS = [
  'waterscopeAccountName',
  'accountType',
  'sensorCount',
  'installedSensorCount',
  'createdAt',
] as const;

/** Every sort column; `revenueT12m` is VIEW_REVENUE only (see `customerSearchUsesRevenue`). */
export const CUSTOMER_SEARCH_SORT_COLUMNS = [...CUSTOMER_BASE_SORT_COLUMNS, 'revenueT12m'] as const;

const minMax = (min: number | undefined, max: number | undefined) =>
  min === undefined || max === undefined || min <= max;

const customerFilterFields = {
  text: searchText.optional().describe('Free-text match against the customer account name'),
  accountType: accountTypeSchema.optional().describe('Utility or Real Estate'),
  realEstateAccountType: realEstateAccountTypeSchema.optional()
    .describe('For Real Estate customers: CMFI or MHP'),
  active: z.boolean().optional().describe('Only active (true) or inactive (false) customers'),
  sensorCountMin: searchCount.optional()
    .describe('At least this many assigned meters (all_sensors_group)'),
  sensorCountMax: searchCount.optional().describe('At most this many assigned meters'),
  installedCountMin: searchCount.optional()
    .describe('At least this many installed meters (in a billing profile)'),
  installedCountMax: searchCount.optional().describe('At most this many installed meters'),
};

// Admin tier (VIEW_REVENUE) only: callers without it get 403 from
// GET /customers/filter and never see these fields in the translator's tool.
const customerRevenueFields = {
  revenueT12mMin: searchMoney.optional().describe(
    'At least this much trailing-12-month revenue, in US dollars: approximate invoiced ' +
      'revenue over the last 12 complete calendar months. Customers with no revenue data never match.',
  ),
  revenueT12mMax: searchMoney.optional().describe(
    'At most this much trailing-12-month revenue, in US dollars (same definition as revenueT12mMin)',
  ),
};

export const customerSearchSchema = z
  .object({
    ...customerFilterFields,
    ...customerRevenueFields,
    ...pagingFields(CUSTOMER_SEARCH_SORT_COLUMNS),
  })
  .strict()
  .refine((s) => minMax(s.sensorCountMin, s.sensorCountMax), {
    message: 'sensorCountMin must not exceed sensorCountMax',
    path: ['sensorCountMin'],
  })
  .refine((s) => minMax(s.installedCountMin, s.installedCountMax), {
    message: 'installedCountMin must not exceed installedCountMax',
    path: ['installedCountMin'],
  })
  .refine((s) => minMax(s.revenueT12mMin, s.revenueT12mMax), {
    message: 'revenueT12mMin must not exceed revenueT12mMax',
    path: ['revenueT12mMin'],
  });

/**
 * The customer spec without the revenue fields or sort, for the translator's
 * tool schema when the caller lacks VIEW_REVENUE. Only its JSON Schema is
 * used (refinements don't reach JSON Schema); emitted filters are validated
 * with `customerSearchSchema` and rejected if `customerSearchUsesRevenue`.
 */
export const customerSearchSchemaWithoutRevenue = z
  .object({ ...customerFilterFields, ...pagingFields(CUSTOMER_BASE_SORT_COLUMNS) })
  .strict();

/** True when the spec filters or sorts by revenue (VIEW_REVENUE required). */
export function customerSearchUsesRevenue(spec: CustomerSearch): boolean {
  return spec.revenueT12mMin !== undefined || spec.revenueT12mMax !== undefined ||
    spec.sortBy === 'revenueT12m';
}

export type CustomerSearch = z.infer<typeof customerSearchSchema>;

/**
 * `GET /customers/filter` rows are the same shape as the customers list, plus
 * trailing-12-month revenue (USD) for VIEW_REVENUE callers only: absent for
 * everyone else, null when the customer has no linked or computed revenue.
 */
export type CustomerSearchRow = Customer & { revenueT12m?: number | null };

export interface CustomerSearchResponse {
  customers: CustomerSearchRow[];
  total: number;
  page: number;
  pageSize: number;
}

const CUSTOMER_SEARCH_FIELDS: SearchFieldKinds = {
  text: 'string',
  accountType: 'string',
  realEstateAccountType: 'string',
  active: 'boolean',
  sensorCountMin: 'number',
  sensorCountMax: 'number',
  installedCountMin: 'number',
  installedCountMax: 'number',
  revenueT12mMin: 'number',
  revenueT12mMax: 'number',
  page: 'number',
  pageSize: 'number',
  sortBy: 'string',
  sortDir: 'string',
};

/** Serialize a customer search to URL params (defaults omitted; see `toSearchParams`). */
export function toCustomerSearchParams(search: CustomerSearch): URLSearchParams {
  return toSearchParams(search, CUSTOMER_SEARCH_FIELDS);
}

/** Parse URL params into a validated customer search (see `parseSearchParams`). */
export function parseCustomerSearchParams(
  params: URLSearchParams,
): ReturnType<typeof customerSearchSchema.safeParse> {
  return parseSearchParams(customerSearchSchema, CUSTOMER_SEARCH_FIELDS, params);
}
